import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api, { getImageUrl } from '@/lib/api';
import { copyShareLink } from '@/lib/generation';
import { toast } from 'sonner';
import {
  ArrowLeft, Film, Play, GripVertical, Video, Clock,
  ChevronRight, LayoutList, Image as ImageIcon, Download, Loader2, Check, X, Link2
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableScene({ scene, projectId, navigate, isPlaying, onPlay }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: scene.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} data-testid={`storyboard-scene-${scene.id}`}
      className={`flex items-stretch gap-0 bg-[#0E0E12] border rounded-lg overflow-hidden transition-all ${isDragging ? 'border-[#FF4500]/50 shadow-lg shadow-[#FF4500]/10' : 'border-white/[0.06] hover:border-white/10'}`}>
      {/* Drag Handle */}
      <div {...attributes} {...listeners}
        className="flex items-center px-2 cursor-grab active:cursor-grabbing hover:bg-white/5 border-r border-white/[0.06]"
        data-testid={`drag-handle-${scene.id}`}>
        <GripVertical className="w-4 h-4 text-[#55555C]" />
      </div>

      {/* Scene Number */}
      <div className="flex items-center justify-center w-10 shrink-0 bg-[#0A0A0E] text-[#55555C] text-xs font-bold">
        {(scene.scene_order ?? 0) + 1}
      </div>

      {/* Thumbnail */}
      <div className="w-40 h-24 shrink-0 bg-black relative group">
        {scene.video_path ? (
          <>
            {scene.first_frame_path && (
              <img src={getImageUrl(scene.first_frame_path)} alt="" className="w-full h-full object-cover" />
            )}
            <button onClick={() => onPlay(scene)} data-testid={`play-scene-${scene.id}`}
              className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
              <Play className="w-8 h-8 text-white fill-white" />
            </button>
            <div className="absolute bottom-1 right-1 bg-black/70 px-1.5 py-0.5 rounded text-[9px] text-white flex items-center gap-1">
              <Video className="w-2.5 h-2.5" /> Ready
            </div>
          </>
        ) : scene.first_frame_path ? (
          <img src={getImageUrl(scene.first_frame_path)} alt="" className="w-full h-full object-cover opacity-60" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-6 h-6 text-[#333]" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 p-3 min-w-0" onClick={() => navigate(`/project/${projectId}/scenes/${scene.id}`)} role="button">
        <h4 className="text-sm font-semibold text-white truncate mb-1">{scene.title || `Scene ${(scene.scene_order ?? 0) + 1}`}</h4>
        <p className="text-xs text-[#8A8A93] line-clamp-2 mb-1.5">{scene.scene_prompt?.slice(0, 100) || 'No description'}</p>
        <div className="flex items-center gap-3 text-[10px] text-[#55555C]">
          {scene.setting && <span>{scene.setting}</span>}
          {scene.mood && <span className="text-[#8A8A93]">{scene.mood}</span>}
          {scene.video_provider && <span className="uppercase text-[#E879F9]">{scene.video_provider}</span>}
        </div>
      </div>

      {/* Arrow */}
      <div className="flex items-center px-3 cursor-pointer hover:bg-white/5"
        onClick={() => navigate(`/project/${projectId}/scenes/${scene.id}`)}>
        <ChevronRight className="w-4 h-4 text-[#55555C]" />
      </div>
    </div>
  );
}

export default function Storyboard() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingScene, setPlayingScene] = useState(null);
  const [roughCutMode, setRoughCutMode] = useState(false);
  const [roughCutIndex, setRoughCutIndex] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const exportPollRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadData = useCallback(async () => {
    try {
      const [projRes, scenesRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/scenes`),
      ]);
      setProject(projRes.data);
      setScenes(scenesRes.data);
    } catch {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Deep link: ?roughcut=1 auto-starts the Rough Cut Preview once videos are loaded
  const autoRoughCutRef = useRef(false);
  useEffect(() => {
    if (autoRoughCutRef.current) return;
    if (searchParams.get('roughcut') === '1' && scenes.filter(s => s.video_path).length > 0) {
      autoRoughCutRef.current = true;
      setRoughCutMode(true);
      setRoughCutIndex(0);
    }
  }, [scenes, searchParams]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = scenes.findIndex(s => s.id === active.id);
    const newIndex = scenes.findIndex(s => s.id === over.id);
    const reordered = arrayMove(scenes, oldIndex, newIndex).map((s, i) => ({ ...s, scene_order: i }));
    setScenes(reordered);

    try {
      await api.put(`/projects/${projectId}/scenes/reorder`, {
        scenes: reordered.map((s, i) => ({ id: s.id, scene_order: i }))
      });
      toast.success('Scene order updated');
    } catch {
      toast.error('Failed to save order');
      loadData();
    }
  };

  const scenesWithVideo = scenes.filter(s => s.video_path);

  const handleRoughCutEnd = () => {
    if (roughCutIndex < scenesWithVideo.length - 1) {
      setRoughCutIndex(prev => prev + 1);
    } else {
      setRoughCutMode(false);
      setRoughCutIndex(0);
    }
  };

  // ── Export Rough Cut ──
  const handleExport = async () => {
    setExporting(true); setExportStatus(null);
    try {
      await api.post(`/projects/${projectId}/export-rough-cut`);
      pollExportStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start export');
      setExporting(false);
    }
  };

  const pollExportStatus = () => {
    if (exportPollRef.current) clearTimeout(exportPollRef.current);
    const poll = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/export-status`, { params: { _t: Date.now() } });
        setExportStatus(res.data);
        if (res.data.status === 'complete') {
          setExporting(false);
          toast.success('Rough cut exported!');
          return;
        } else if (res.data.status === 'failed') {
          setExporting(false);
          toast.error(res.data.error || 'Export failed');
          return;
        }
      } catch (err) {
        console.error('Export poll error:', err);
      }
      exportPollRef.current = setTimeout(poll, 3000);
    };
    exportPollRef.current = setTimeout(poll, 2000);
  };

  const handleDownloadExport = () => {
    if (!exportStatus?.output_path) return;
    const url = getImageUrl(exportStatus.output_path);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project?.name || 'rough-cut'}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Check for existing export on load
  useEffect(() => {
    const checkExport = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/export-status`, { params: { _t: Date.now() } });
        if (res.data.status === 'processing') {
          setExporting(true);
          setExportStatus(res.data);
          pollExportStatus();
        } else if (res.data.status === 'complete') {
          setExportStatus(res.data);
        }
      } catch {}
    };
    checkExport();
    return () => { if (exportPollRef.current) clearTimeout(exportPollRef.current); };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="min-h-screen bg-[#060608]">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="ff-card p-8 h-48 ff-pulse" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#060608]" data-testid="storyboard-page">
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate(`/project/${projectId}`)} className="p-2 rounded-lg hover:bg-white/5 text-[#8A8A93]">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Storyboard
            </h1>
            <p className="text-[#8A8A93] text-sm mt-1">{project?.name} — {scenes.length} scene{scenes.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="copy-storyboard-link"
              onClick={() => copyShareLink(`/project/${projectId}/storyboard${scenesWithVideo.length > 1 ? '?roughcut=1' : ''}`, 'Storyboard link')}
              title={scenesWithVideo.length > 1 ? 'Copy a link that opens the rough cut preview' : 'Copy a link to this storyboard'}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-[#8A8A93] hover:text-[#00F0FF] hover:bg-white/5 transition-all">
              <Link2 className="w-4 h-4" /> <span className="hidden sm:inline">Copy Link</span>
            </button>
            {scenesWithVideo.length >= 1 && (
              <button data-testid="export-rough-cut-btn"
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 border border-[#10B981]/30 disabled:opacity-40 transition-all">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {exporting ? 'Exporting...' : 'Export .mp4'}
              </button>
            )}
            {scenesWithVideo.length > 1 && (
              <button data-testid="rough-cut-btn"
                onClick={() => { setRoughCutMode(true); setRoughCutIndex(0); }}
                className="ff-btn-primary flex items-center gap-2">
                <Play className="w-4 h-4" /> Rough Cut Preview
              </button>
            )}
          </div>
        </div>

        {/* Export Progress / Download */}
        {(exporting || exportStatus?.status === 'complete') && (
          <div className={`mb-6 rounded-xl border overflow-hidden ${exportStatus?.status === 'complete' ? 'border-[#10B981]/30 bg-[#10B981]/5' : 'border-[#E879F9]/20 bg-[#E879F9]/5'}`}
            data-testid="export-status-bar">
            <div className="px-5 py-4">
              {exportStatus?.status === 'complete' ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
                      <Check className="w-4 h-4 text-[#10B981]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">Rough Cut Ready</p>
                      <p className="text-[11px] text-[#8A8A93]">
                        {exportStatus.scene_count} scene{exportStatus.scene_count !== 1 ? 's' : ''} &middot; {exportStatus.file_size_mb}MB
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={handleDownloadExport} data-testid="download-export-btn"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#10B981] hover:bg-[#10B981]/80 text-white transition-all">
                      <Download className="w-4 h-4" /> Download .mp4
                    </button>
                    <button onClick={() => setExportStatus(null)} className="p-1.5 rounded-lg text-[#55555C] hover:text-white hover:bg-white/5">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#E879F9]" />
                      <span className="text-sm font-medium text-white">{exportStatus?.progress || 'Starting export...'}</span>
                    </div>
                    <span className="text-xs text-[#8A8A93] tabular-nums">{exportStatus?.percent || 0}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${exportStatus?.percent || 0}%`, background: 'linear-gradient(90deg, #E879F9, #FF4500)' }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rough Cut Player */}
        {roughCutMode && scenesWithVideo.length > 0 && (
          <div className="mb-8 bg-black rounded-xl overflow-hidden border border-white/[0.06]" data-testid="rough-cut-player">
            <div className="relative">
              <video
                key={scenesWithVideo[roughCutIndex]?.id}
                autoPlay
                onEnded={handleRoughCutEnd}
                className="w-full max-h-[480px] mx-auto"
                src={getImageUrl(scenesWithVideo[roughCutIndex]?.video_path)}
              />
              <div className="absolute top-3 left-3 bg-black/70 px-3 py-1 rounded-lg text-xs text-white flex items-center gap-2">
                <Film className="w-3.5 h-3.5 text-[#FF4500]" />
                Scene {(scenesWithVideo[roughCutIndex]?.scene_order ?? 0) + 1}: {scenesWithVideo[roughCutIndex]?.title}
                <span className="text-[#55555C]">({roughCutIndex + 1}/{scenesWithVideo.length})</span>
              </div>
            </div>
            {/* Timeline scrubber */}
            <div className="flex gap-0.5 p-2 bg-[#0A0A0E]">
              {scenesWithVideo.map((s, i) => (
                <button key={s.id} onClick={() => setRoughCutIndex(i)}
                  data-testid={`rough-cut-scene-${i}`}
                  className={`flex-1 h-1.5 rounded-full transition-all ${i === roughCutIndex ? 'bg-[#FF4500]' : i < roughCutIndex ? 'bg-[#FF4500]/40' : 'bg-[#1A1A20]'}`} />
              ))}
            </div>
            <div className="flex justify-end p-2">
              <button onClick={() => setRoughCutMode(false)} className="text-xs text-[#8A8A93] hover:text-white transition-colors">
                Close Preview
              </button>
            </div>
          </div>
        )}

        {/* Single scene video player */}
        {playingScene && !roughCutMode && (
          <div className="mb-8 bg-black rounded-xl overflow-hidden border border-white/[0.06]" data-testid="scene-video-player">
            <video controls autoPlay className="w-full max-h-[480px]" src={getImageUrl(playingScene.video_path)} />
            <div className="flex items-center justify-between p-2 bg-[#0A0A0E]">
              <span className="text-xs text-[#8A8A93]">Scene {(playingScene.scene_order ?? 0) + 1}: {playingScene.title}</span>
              <button onClick={() => setPlayingScene(null)} className="text-xs text-[#8A8A93] hover:text-white">Close</button>
            </div>
          </div>
        )}

        {/* Stats bar */}
        <div className="flex items-center gap-6 mb-6 text-xs text-[#55555C]">
          <div className="flex items-center gap-1.5">
            <LayoutList className="w-3.5 h-3.5" /> {scenes.length} scenes
          </div>
          <div className="flex items-center gap-1.5">
            <Video className="w-3.5 h-3.5 text-[#10B981]" /> {scenesWithVideo.length} videos ready
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Drag to reorder
          </div>
        </div>

        {/* Sortable Scene List */}
        {scenes.length === 0 ? (
          <div className="text-center py-20 text-[#55555C]">
            <Film className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-base">No scenes yet</p>
            <p className="text-sm mt-1">Go to Production Mode to start extracting scenes</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={scenes.map(s => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {scenes.map(scene => (
                  <SortableScene
                    key={scene.id}
                    scene={scene}
                    projectId={projectId}
                    navigate={navigate}
                    isPlaying={playingScene?.id === scene.id}
                    onPlay={setPlayingScene}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </main>
    </div>
  );
}
